import { useState, useEffect, useRef } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { checkAdminStatus } from '../firebase/helpers';

// Cache admin status to avoid repeated Firestore reads
const adminStatusCache = new Map();
const CACHE_DURATION = 300000; // 5 minutes

export const useAdminAuth = () => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [statusDetails, setStatusDetails] = useState(null);
  const mountedRef = useRef(true);

  // Clear cache for a specific user
  const clearCache = (userId) => {
    if (userId) {
      adminStatusCache.delete(userId);
    } else {
      adminStatusCache.clear();
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!mountedRef.current) return;

      if (user) {
        setAdmin(user);
        
        // Check cache first
        const cached = adminStatusCache.get(user.uid);
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
          setIsAdmin(cached.data.isAdmin);
          setStatusDetails(cached.data);
          setLoading(false);
        } else {
          // Check admin status (checks both adminUsers and users collections)
          try {
            const adminStatus = await checkAdminStatus(user.uid, user.email);
            
            if (mountedRef.current) {
              setIsAdmin(adminStatus.isAdmin);
              setStatusDetails(adminStatus);
              
              // Cache the result
              adminStatusCache.set(user.uid, {
                data: adminStatus,
                timestamp: Date.now()
              });
            }
          } catch (error) {
            console.error('Error checking admin status:', error);
            if (mountedRef.current) {
              setIsAdmin(false);
              setStatusDetails({ isAdmin: false, error: error.message });
            }
          }
        }
      } else {
        setAdmin(null);
        setIsAdmin(false);
        setStatusDetails(null);
      }
      
      if (mountedRef.current) {
        setLoading(false);
      }
    });

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Check admin status (checks both adminUsers and users collections)
      const adminStatus = await checkAdminStatus(userCredential.user.uid, email);
      
      if (!adminStatus.isAdmin) {
        await signOut(auth);
        clearCache(userCredential.user.uid);
        
        if (adminStatus.requiresUidFix) {
          throw new Error(adminStatus.message || 'Admin record mismatch. Please recreate your admin document using your UID.');
        }
        throw new Error('Access denied. This account is not an admin.');
      }
      
      // Cache the admin status
      adminStatusCache.set(userCredential.user.uid, {
        data: adminStatus,
        timestamp: Date.now()
      });
      
      setAdmin(userCredential.user);
      setIsAdmin(true);
      setStatusDetails(adminStatus);
      
      return userCredential.user;
    } catch (error) {
      setLoading(false);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const currentUserId = auth.currentUser?.uid;
      await signOut(auth);
      clearCache(currentUserId);
      setAdmin(null);
      setIsAdmin(false);
      setStatusDetails(null);
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  };

  // Refresh admin status (clears cache and rechecks)
  const refreshAdminStatus = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return false;
    }

    try {
      clearCache(currentUser.uid);
      const adminStatus = await checkAdminStatus(currentUser.uid, currentUser.email);
      
      setIsAdmin(adminStatus.isAdmin);
      setStatusDetails(adminStatus);
      
      // Cache the result
      adminStatusCache.set(currentUser.uid, {
        data: adminStatus,
        timestamp: Date.now()
      });
      
      return adminStatus.isAdmin;
    } catch (error) {
      console.error('Error refreshing admin status:', error);
      return false;
    }
  };

  return { 
    admin, 
    isAdmin, 
    loading, 
    login, 
    logout, 
    statusDetails,
    refreshAdminStatus,
    clearCache
  };
};

